<template>
  <component :is="baseTag" class="btn" :class="classes.btn" v-bind="{ ...$attrs, ...attrs }">
    <span class="btn__content" :class="classes.content">
      <BaseIcon v-if="iconName" :icon-name="iconName">
        <component :is="currentIcon" />
      </BaseIcon>
      <slot v-else />
    </span>
  </component>
</template>

<script setup lang="ts">
import { AnchorHTMLAttributes, computed } from 'vue';
import { RouteLocationRaw } from 'vue-router';
import BaseIcon from '@/components/atoms/BaseIcon/BaseIcon.vue';
import IconChevronLeft from '@/components/atoms/icons/IconChevronLeft.vue';
import IconChevronRight from '@/components/atoms/icons/IconChevronRight.vue';
import IconChevronRightCircleOutline from '@/components/atoms/icons/IconChevronRightCircleOutline.vue';
import IconClose from '@/components/atoms/icons/IconClose.vue';

interface Props {
  small?: boolean;
  large?: boolean;
  color?: 'default' | 'primary' | 'secondary' | 'grey' | 'light-grey' | 'tertiary';
  rounded?: boolean;
  outlined?: boolean;
  fluid?: boolean;
  disabled?: boolean;
  icon?: boolean | string;
  to?: RouteLocationRaw;
  href?: AnchorHTMLAttributes['href'];
}

const props = withDefaults(defineProps<Props>(), {
  small: false,
  large: false,
  color: 'default',
  rounded: false,
  outlined: false,
  fluid: false,
  disabled: false,
  icon: false,
  to: undefined,
  href: undefined,
});

const classes = computed(() => ({
  btn: {
    // size
    'size--default': !props.large && !props.small,
    'size--small': props.small,
    'size--large': props.large,
    // color
    primary: props.color === 'primary' && !props.outlined,
    secondary: props.color === 'secondary' && !props.outlined,
    grey: props.color === 'grey' && !props.outlined,
    'light-grey': props.color === 'light-grey' && !props.outlined,
    'primary--text': props.color === 'primary' && props.outlined,
    'secondary--text': props.color === 'secondary' && props.outlined,
    'grey--text': props.color === 'grey' && props.outlined,
    'grey--disabled-text': props.color === 'grey' && props.disabled && !props.outlined,
    'light-grey--text': props.color === 'light-grey' && props.outlined,
    tertiary: props.color === 'tertiary',
    // shape
    'btn--icon': !!props.icon,
    'btn--round': !!props.icon,
    'btn--rounded': props.rounded,
    'btn--outlined': props.outlined,
    'btn--fluid': props.fluid,
  },
  content: {},
}));

const iconName = computed(() => (typeof props.icon === 'string' ? props.icon : null));
const currentIcon = computed(() => {
  switch (iconName.value) {
    case 'chevron-left':
      return IconChevronLeft;
    case 'chevron-right':
      return IconChevronRight;
    case 'chevron-right-circle-outline':
      return IconChevronRightCircleOutline;
    case 'close':
      return IconClose;
  }
  return null;
});

const baseTag = computed(() => {
  if (props.to) return 'router-link';
  if (props.href) return 'a';
  return 'button';
});

const attrs = computed(() => {
  if (baseTag.value === 'router-link') return { to: props.to };
  if (baseTag.value === 'a') return { href: props.href };
  return { type: 'button', disabled: props.disabled };
});
</script>

<style scoped lang="scss">
button {
  background-color: transparent;
  border-style: none;
}

.btn {
  align-items: center;
  // border-radius: 4px;
  display: inline-flex;
  flex: 0 0 auto;
  font-weight: 300;
  letter-spacing: 0.0892857143em;
  justify-content: center;
  outline: 0;
  position: relative;
  text-decoration: none;
  text-indent: 0.0892857143em;
  text-transform: uppercase;
  transition-duration: 0.28s;
  transition-property: box-shadow, transform, opacity;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  vertical-align: middle;
  white-space: nowrap;
  color: $color-base;

  &__content {
    align-items: center;
    display: flex;
    flex: 1 0 auto;
    justify-content: inherit;
    line-height: normal;
    position: relative;
    transition: inherit;
    transition-property: opacity;
  }

  &--icon {
    min-height: 0;
    min-width: 0;
    padding: 0;
    color: inherit;

    &.size {
      &--default {
        height: 30px;
        width: 30px;

        &::v-deep(svg) {
          height: 24px;
          width: 24px;
        }
      }

      &--small {
        height: 22px;
        width: 22px;

        &::v-deep(svg) {
          height: 18px;
          width: 18px;
        }
      }

      &--large {
        height: 40px;
        width: 40px;

        &::v-deep(svg) {
          height: 30px;
          width: 30px;
        }
      }
    }
  }

  &--rounded {
    border-radius: 999px;
  }

  &--outlined {
    border: thin solid currentColor;
  }

  &--fluid {
    width: 100%;
  }

  &.size {
    &--default {
      &:not(.btn--round) {
        height: 30px;
        min-width: 130px;
        padding: 0 16px;
        font-size: 12px;
      }
    }

    &--small {
      &:not(.btn--round) {
        height: 22px;
        min-width: 130px;
        padding: 0 11px;
        font-size: 10px;
      }
    }

    &--large {
      &:not(.btn--round) {
        height: 40px;
        min-width: 130px;
        padding: 0 20px;
        font-size: 14px;
      }
    }
  }

  &.primary {
    color: $color-base;
    background-color: $color-primary !important;
    border-color: $color-primary !important;

    &--text {
      color: $color-primary !important;
      caret-color: $color-primary !important;
    }
  }

  &.secondary {
    color: $color-base;
    background-color: $color-secondary !important;
    border-color: $color-secondary !important;

    &--text {
      color: $color-secondary !important;
      caret-color: $color-secondary !important;
    }
  }

  &.grey {
    color: $color-base;
    background-color: $color-grey !important;
    border-color: $color-grey !important;

    &--text {
      color: $color-grey !important;
      caret-color: $color-grey !important;
    }

    &--disabled-text {
      color: $color-grey-light !important;
      caret-color: $color-grey-light !important;
    }
  }

  &.light-grey {
    color: $color-base;
    background-color: $color-grey-light !important;
    border-color: $color-grey-light !important;

    &--text {
      color: $color-grey-light !important;
      caret-color: $color-grey-light !important;
    }
  }

  &.tertiary {
    color: $bg-base;
    background-color: $color-tertiary !important;
    border-color: $color-tertiary !important;

    &--text {
      color: $color-tertiary !important;
      caret-color: $color-tertiary !important;
    }
  }
}
</style>
